import React, { useEffect, useState } from 'react';
import { Monitor, Radio, Smartphone, Tablet, WifiOff } from 'lucide-react';
import { API_BASE } from '@/lib/api';
import { timeAgo } from '@/lib/format';

const DEVICE_ICONS = { desktop: Monitor, tablet: Tablet, mobile: Smartphone };
const testKey = (value) => String(value || 'unknown').replace(/[^a-zA-Z0-9-]/g, '-');

export const OnlineLoomUsers = ({ onCountChange }) => {
  const [users, setUsers] = useState([]);
  const [status, setStatus] = useState('connecting');
  const [, setTick] = useState(0);

  useEffect(() => {
    const stream = new EventSource(`${API_BASE}/admin/users/online`, { withCredentials: true });
    const onPresence = (event) => {
      try {
        const payload = JSON.parse(event.data);
        const next = payload.users || [];
        setUsers(next);
        onCountChange?.(payload.count ?? next.length);
        setStatus('live');
      } catch {
        setStatus('reconnecting');
      }
    };
    stream.addEventListener('presence', onPresence);
    stream.onopen = () => setStatus('live');
    stream.onerror = () => setStatus('reconnecting');
    return () => {
      stream.removeEventListener('presence', onPresence);
      stream.close();
    };
  }, [onCountChange]);

  useEffect(() => {
    const timer = window.setInterval(() => setTick((value) => value + 1), 10_000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <section className="overflow-hidden rounded-md border border-border bg-card shadow-panel" data-testid="online-loom-users-panel">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-panel-header/40 px-4 py-3">
        <div>
          <div className="flex items-center gap-2 text-[13px] font-semibold">
            <Radio className="h-4 w-4 text-emerald-600" /> Online in The Loom
          </div>
          <p className="mt-0.5 text-[11px] text-muted-foreground">Active heartbeat received within the last 90 seconds.</p>
        </div>
        <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="online-loom-stream-status">
          {status === 'live' ? <Radio className="h-3 w-3 text-emerald-600" /> : <WifiOff className="h-3 w-3 text-amber-600" />}
          {status === 'live' ? 'Live' : status === 'connecting' ? 'Connecting…' : 'Reconnecting…'}
        </span>
      </header>
      <div className="grid grid-cols-[minmax(12rem,1.2fr)_minmax(12rem,1.4fr)_minmax(11rem,1fr)_9rem_8rem] gap-3 border-b border-border bg-muted/30 px-4 py-2 text-[10px] font-semibold uppercase text-muted-foreground">
        <span>Name</span><span>Email</span><span>Current area</span><span>Last activity</span><span>Device</span>
      </div>
      <div className="divide-y divide-border" data-testid="online-loom-users-list">
        {users.map((user) => {
          const key = testKey(user.user_id || user.email);
          const DeviceIcon = DEVICE_ICONS[user.device_type] || Monitor;
          return (
            <div key={key} className="grid grid-cols-[minmax(12rem,1.2fr)_minmax(12rem,1.4fr)_minmax(11rem,1fr)_9rem_8rem] items-center gap-3 px-4 py-3 text-[11.5px]" data-testid={`online-loom-user-row-${key}`}>
              <span className="truncate font-semibold" data-testid={`online-loom-user-name-${key}`}>{user.name || 'Unnamed user'}</span>
              <span className="truncate text-muted-foreground" data-testid={`online-loom-user-email-${key}`}>{user.email}</span>
              <span className="truncate" data-testid={`online-loom-user-area-${key}`}>{user.current_area}</span>
              <span className="text-muted-foreground" title={user.last_activity} data-testid={`online-loom-user-activity-${key}`}>{timeAgo(user.last_activity)}</span>
              <span className="inline-flex items-center gap-1.5 capitalize" data-testid={`online-loom-user-device-${key}`}><DeviceIcon className="h-3.5 w-3.5" /> {user.device_type}</span>
            </div>
          );
        })}
        {!users.length && (
          <div className="px-4 py-10 text-center text-[12px] text-muted-foreground" data-testid="online-loom-users-empty">No writers are active in The Loom right now.</div>
        )}
      </div>
    </section>
  );
};